import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet } from 'react-router'
import { Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { contextCompanyIdSelector, contextCompanySelector } from '../redux/context/company/selectors'
import { filtersSelector } from '../redux/context/filters/selectors'
import { setBudgetingScenarioFilter, updateDimensionFilterAction } from '../redux/context/filters/actions'
import { contextRequest } from '../redux/context/actions'
import { getCompanySettingsRequest } from '../redux/context/companySettings/actions'
import { AppDispatch } from '../redux/store'
import Loading from '../shared/components/Loading'
import RequestError from '../shared/components/RequestError'
import { useCtxFiscalYears } from '../features/fiscalYear/queries/report/useCtxFiscalYears'
import { useCtxScenarios } from '../features/scenario/queries/useCtxScenarios'
import { useCtxDimensions } from '../features/dimension/queries/useCtxDimensions'

const { Text, Title } = Typography

function CompanyContextManager() {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const companyId = useSelector(contextCompanyIdSelector)
  const company = useSelector(contextCompanySelector)
  const { budgetingScenario, fiscalYear, dimensions: filterDimensions, dimensionsQuery } = useSelector(filtersSelector)

  // Fetch data using custom hooks (assuming these exist or you'll create them)
  const {
    data: scenarios,
    isLoading: scenariosLoading,
    error: scenariosError,
    refetch: refetchScenarios
  } = useCtxScenarios()
  const {
    data: fiscalYears,
    isLoading: fiscalLoading,
    error: fiscalError,
    refetch: refetchFiscal
  } = useCtxFiscalYears()
  const {
    data: dimensionList,
    isLoading: dimensionsLoading,
    error: dimensionsError,
    refetch: refetchDimensions
  } = useCtxDimensions()

  // Rebuild dimension query when dimension data loads for the first time.
  // This handles restricted users whose dimension filter was set at login
  // before dimension data was available in the React Query cache.
  useEffect(() => {
    if (filterDimensions?.length && !dimensionsQuery && dimensionList?.length) {
      dispatch(updateDimensionFilterAction(filterDimensions))
      // Company settings may already have been fetched without the dimension filter
      // (setContextCompany fires before dimension data is available), so refetch now.
      dispatch(contextRequest(getCompanySettingsRequest))
    }
  }, [dimensionList, filterDimensions, dimensionsQuery, dispatch])

  useEffect(() => {
    // Wait for all data to load and check for errors
    if (scenariosLoading || fiscalLoading || scenariosError || fiscalError || !scenarios || !fiscalYears) {
      return
    }

    // Validate and set default budgeting scenario
    const isValidScenario = scenarios.some(scenario => scenario.id === budgetingScenario?.id)
    if (!isValidScenario && scenarios.length > 0) {
      dispatch(setBudgetingScenarioFilter(scenarios.find(s => s.id === null)))
    }
  }, [
    companyId,
    scenarios,
    budgetingScenario?.id,
    scenariosLoading,
    scenariosError,
    fiscalYears,
    fiscalYear?.id,
    fiscalLoading,
    fiscalError,
    dispatch
  ])

  // Handle loading and error states
  if (scenariosLoading || fiscalLoading || dimensionsLoading) {
    return (
      <Loading>
        <div style={{ textAlign: 'center' }}>
          {company?.name && (
            <Title level={4} style={{ margin: '16px 0 4px' }}>
              {company.name}
            </Title>
          )}
          <Text type="secondary">{t('global:loadingCompanyData')}</Text>
        </div>
      </Loading>
    )
  }

  if (scenariosError) return <RequestError error={scenariosError} refetch={refetchScenarios} />
  if (fiscalError) return <RequestError error={fiscalError} refetch={refetchFiscal} />
  if (dimensionsError) return <RequestError error={dimensionsError} refetch={refetchDimensions} />

  return <Outlet />
}

export default CompanyContextManager
